import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApiKeyManager } from '../ApiKey/ApiKeyManagerProvider';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { trackEvent } from '../../utils/analytics';
import { ArrowRightOutlined } from '@ant-design/icons';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { generateAgentToAgentSchema, JsonLd } from '../../utils/structuredData';
import A2AHero from './sections/A2AHero';
import A2ATwoInterfaces from './sections/A2ATwoInterfaces';
import A2AStat from './sections/A2AStat';
import A2AIntegrations from './sections/A2AIntegrations';
import A2AHowItWorks from './sections/A2AHowItWorks';
import A2AFAQ from './sections/A2AFAQ';
import A2AClose from './sections/A2AClose';
import MobileStickyCta from '../Marketing/MobileStickyCta';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './AgentToAgentPage.css';

/**
 * AgentToAgentPage — public marketing page at /agent-to-agent.
 *
 * Thesis: every software will have TWO interfaces — one for humans, one for
 * agents. CellCog commits to exposing its ENTIRE platform to other agents
 * (OpenClaw, Claude Code, Cursor, Hermes, Linear) so any agent can use every
 * capability, just like a human. Reuses the `.aie-landing` design system; dark-only.
 *
 * Conversion flow (the page's one job: visitor → account → API key in hand):
 *  - logged-in  → primary CTA opens the ApiKeyManager modal in place
 *  - logged-out → primary CTA stashes `/agent-to-agent?intent=get_api_key` via
 *    persistentNavigation (the real post-auth routing rail — the old `?return=`
 *    query param was dead code; /signup never read it), routes to /signup, and
 *    on return the intent effect below auto-opens the key modal.
 */
const AgentToAgentPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { open: openApiKeyManager } = useApiKeyManager();

  const ctaLabel = currentUser ? 'Get your API key' : 'Sign up to get API key';

  const handlePrimary = useCallback((location) => {
    trackEvent('cta_click', {
      cta_name: 'get_api_key',
      location: typeof location === 'string' ? location : 'page',
      page: 'agent-to-agent',
    });
    if (!currentUser) {
      saveIntendedUrl('/agent-to-agent?intent=get_api_key');
      navigate('/signup');
      return;
    }
    openApiKeyManager();
  }, [currentUser, navigate, openApiKeyManager]);

  // Post-signup auto-open (mirrors /openclaw): consume ?intent=get_api_key,
  // clean the URL so refresh doesn't re-trigger, then open the key modal.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('intent') === 'get_api_key') {
      params.delete('intent');
      const url = new URL(window.location);
      url.search = params.toString();
      window.history.replaceState({}, document.title, url.toString());
      openApiKeyManager();
    }
  }, [currentUser, openApiKeyManager]);

  const handleSeeHow = () => {
    const t = document.getElementById('a2a-how');
    if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const seo = {
    title: 'API for AI Agents — OpenClaw, Claude Code, Cursor & Linear | CellCog',
    description:
      'Give any AI agent every CellCog capability with one API key: deep research, documents, video, dashboards, delegated from OpenClaw, Claude Code, Cursor, Hermes, or Linear and returned as finished work. Try it free, no credit card needed.',
  };
  const canonicalUrl = `${SEO_DEFAULTS.url}/agent-to-agent`;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/agent-to-agent/og-agent-to-agent.png`} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/agent-to-agent/og-agent-to-agent.png`} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateAgentToAgentSchema()} />

      <div className="aie-landing a2a-page dark">
        <A2AHero ctaLabel={ctaLabel} onPrimary={handlePrimary} onSeeHow={handleSeeHow} />
        <A2ATwoInterfaces />
        <A2AStat />
        <div className="aie-divider" />
        <A2AIntegrations />
        <A2AHowItWorks />
        {/* Mid-page CTA repeat (Aug 30 depth pass, Nitish's ask): the page ran ~5,000px
            between the hero CTA and the closer on desktop. Same label + destination as
            the hero (repeat ONE goal, never add a second — Aug 22 conversion research);
            placed after the code sample, the moment an agent-builder is convinced.
            Hidden under 768px where the mobile sticky CTA already covers it. */}
        <div className="a2a-midcta">
          <button type="button" className="aie-btn aie-btn-primary" onClick={() => handlePrimary('mid_how_it_works')}>
            {ctaLabel} <ArrowRightOutlined />
          </button>
          <p className="a2a-midcta__note">
            Try it free, no credit card needed. Every account starts with 200 credits.
          </p>
        </div>
        <A2AFAQ />
        <A2AClose ctaLabel={ctaLabel} onPrimary={handlePrimary} />
        <MobileStickyCta label={ctaLabel} onClick={() => handlePrimary('sticky_bar')} />
      </div>
    </>
  );
};

export default AgentToAgentPage;
